import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { InteractiveTour } from "@/components/InteractiveTour";
import axios from "axios";
import { ArrowRight, Check, CheckCircle2, Quote, Wallet, BellRing, CalendarCheck, Building2, ScanLine, Boxes, ArrowLeftRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useApp } from "@/context/AppContext";
import { LangToggle } from "@/components/layout/AppShell";
import { Logo } from "@/components/Logo";
import { COPY, TESTIMONIALS, FAQ } from "@/content/landing";

const ICONS = [Wallet, BellRing, CalendarCheck, Building2, ScanLine, Boxes];
const CTA = "group inline-flex items-center gap-2 rounded-full bg-[#E30613] px-6 py-3 text-sm font-semibold text-white shadow-[0_8px_24px_-8px_rgba(227,6,19,0.6)] transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-[#B8000F]";

const Frame = ({ src, alt, className = "" }) => (
  <div className={`overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_30px_80px_-30px_rgba(17,20,24,0.45)] ${className}`}>
    <div className="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-3 py-2"><span className="h-2.5 w-2.5 rounded-full bg-[#E30613]" /><span className="h-2.5 w-2.5 rounded-full bg-slate-300" /><span className="h-2.5 w-2.5 rounded-full bg-slate-300" /><span className="ms-3 text-[10px] text-slate-400">mohassib.tn/app</span></div>
    <img src={src} alt={alt} className="block w-full" loading="lazy" />
  </div>
);

const DemoButton = ({ className, testid }) => {
  const { t, startDemo } = useApp();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await startDemo(); nav("/app"); } catch (e) { toast.error(e?.response?.data?.detail || t("demo_unavailable")); setBusy(false); }
  };
  return <button onClick={go} disabled={busy} className={className} data-testid={testid}>{busy ? t("loading") : t("demo_try")}</button>;
};

const Header = ({ c }) => {
  const { t } = useApp();
  return (
    <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center gap-6 px-6 py-3">
        <Logo size={34} />
        <nav className="ms-6 hidden items-center gap-6 text-sm text-slate-600 md:flex">
          {Object.entries(c.nav).map(([k, v]) => <a key={k} href={`#${k}`} className="transition-colors hover:text-[#E30613]" data-testid={`landing-nav-${k}`}>{v}</a>)}
        </nav>
        <div className="ms-auto flex items-center gap-2">
          <LangToggle />
          <Link to="/sign-in" data-testid="landing-signin-btn" className="hidden rounded-full px-4 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 sm:block">{t("sign_in")}</Link>
          <Link to="/sign-up" data-testid="landing-signup-btn" className="rounded-full bg-slate-900 px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-[#E30613]">{t("sign_up")}</Link>
        </div>
      </div>
    </header>
  );
};

const Hero = ({ c }) => {
  const { t } = useApp();
  return (
    <section className="relative overflow-hidden">
      <div className="pointer-events-none absolute -top-40 end-[-10%] h-[520px] w-[520px] rounded-full bg-[#E30613]/10 blur-3xl" />
      <div className="mx-auto grid max-w-7xl gap-14 px-6 pb-20 pt-16 lg:grid-cols-[1fr_1.15fr] lg:pt-24">
        <div className="fade-up">
          <span className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-[#B8000F]" data-testid="landing-note"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#E30613]" />{c.note}</span>
          <h1 className="mt-6 text-4xl font-extrabold leading-[1.04] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="landing-h1">
            {c.h1a}<br />{c.h1b} <span className="text-[#E30613]">{c.h1c}</span>
          </h1>
          <p className="mt-6 max-w-xl text-base text-slate-600 md:text-lg">{c.sub}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/sign-up" data-testid="hero-start-trial-btn" className={CTA}>{t("start_trial")}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180" /></Link>
            <DemoButton testid="hero-demo-btn" className="rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-800 transition-colors hover:border-slate-900" />
          </div>
          <ul className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-500">{c.trust.map((x) => <li key={x} className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600" />{x}</li>)}</ul>
        </div>
        <div className="relative fade-up" style={{ animationDelay: "150ms" }}>
          <Frame src="/screens/dashboard.png" alt="Tableau de bord Mohassib" className="lg:-me-24" />
          <div className="absolute -bottom-6 start-[-1rem] flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 pe-5 shadow-xl fade-up" style={{ animationDelay: "450ms" }}>
            <CheckCircle2 className="h-8 w-8 text-emerald-600" /><div><div className="num text-xs font-semibold text-slate-900">FA-2026-00014</div><div className="text-[11px] text-emerald-700">{t("st_accepted")}</div></div>
          </div>
          <div className="absolute -top-5 end-4 hidden items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-xs font-medium text-white shadow-xl sm:flex fade-up" style={{ animationDelay: "650ms" }}>
            <ArrowLeftRight className="h-3.5 w-3.5 text-[#FF5A63]" />9 / 12 {t("bank_auto_matched")}
          </div>
        </div>
      </div>
    </section>
  );
};

const Stats = ({ c }) => (
  <section className="bg-slate-900 text-white">
    <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px bg-white/10 lg:grid-cols-4">
      {c.stats.map(([k, v]) => <div key={k} className="bg-slate-900 px-6 py-8"><div className="text-2xl font-bold text-white">{k}</div><div className="mt-1 text-sm text-slate-400">{v}</div></div>)}
    </div>
  </section>
);

const Showcase = ({ c }) => (
  <section id="features" className="mx-auto max-w-7xl space-y-28 px-6 py-24">
    {c.showcase.map((s, i) => (
      <div key={s.title} className={`grid items-center gap-12 lg:grid-cols-[1fr_1.35fr] ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`landing-showcase-${i}`}>
        <div>
          <div className="eyebrow !text-[#E30613]">{s.eyebrow}</div>
          <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{s.title}</h2>
          <ul className="mt-6 space-y-3">{s.points.map((p) => <li key={p} className="flex gap-3 text-slate-600"><span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-[#E30613] text-white"><Check className="h-3.5 w-3.5" /></span>{p}</li>)}</ul>
        </div>
        <Frame src={s.img} alt={s.eyebrow} className="transition-transform duration-500 hover:-translate-y-1" />
      </div>
    ))}
  </section>
);

const Features = ({ c }) => (
  <section className="border-y border-slate-200 bg-white">
    <div className="mx-auto grid max-w-7xl gap-px bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
      {c.features.map(([h, p], i) => {
        const I = ICONS[i];
        return (
          <div key={h} className="group bg-white p-8 transition-colors hover:bg-red-50/40" data-testid={`landing-feature-${i}`}>
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-white transition-colors group-hover:bg-[#E30613]"><I className="h-5 w-5" /></span>
            <h3 className="mt-5 text-base font-semibold text-slate-900">{h}</h3>
            <p className="mt-2 text-sm text-slate-600">{p}</p>
          </div>
        );
      })}
    </div>
  </section>
);

const PlanCard = ({ p, c, yearly, hot }) => (
  <div className={`relative flex flex-col rounded-2xl border p-7 ${hot ? "border-slate-900 bg-slate-900 text-white shadow-2xl lg:-my-4" : "border-slate-200 bg-white"}`} data-testid={`landing-plan-${p.code}`}>
    {hot && <span className="absolute -top-3 start-7 rounded-full bg-[#E30613] px-3 py-1 text-[11px] font-bold text-white">{c.popular}</span>}
    <h3 className="text-lg font-bold">{p.name}</h3>
    <div className="mt-4 flex items-baseline gap-1.5"><span className="num text-4xl font-bold" data-testid={`landing-plan-price-${p.code}`}>{yearly ? p.price_year : p.price_month}</span><span className={`text-sm ${hot ? "text-slate-400" : "text-slate-500"}`}>{yearly ? c.per_year : c.per_month}</span></div>
    <p className={`mt-1 text-xs ${hot ? "text-slate-400" : "text-slate-500"}`}>{p.max_users} {c.users} · {p.max_docs_month} {c.docs}</p>
    <ul className="mt-6 flex-1 space-y-2.5 text-sm">
      {c.included.map((x) => <li key={x} className="flex gap-2"><Check className="h-4 w-4 shrink-0 text-[#E30613]" />{x}</li>)}
      <li className={`flex gap-2 ${p.stock ? "" : "opacity-40 line-through"}`}><Check className="h-4 w-4 shrink-0 text-[#E30613]" />{c.stock}</li>
      <li className={`flex gap-2 ${p.accounting ? "" : "opacity-40 line-through"}`}><Check className="h-4 w-4 shrink-0 text-[#E30613]" />{c.accounting}</li>
    </ul>
    <Link to="/sign-up" className={`mt-7 rounded-full py-3 text-center text-sm font-semibold transition-colors ${hot ? "bg-[#E30613] text-white hover:bg-[#B8000F]" : "border border-slate-300 text-slate-900 hover:border-slate-900"}`} data-testid={`landing-plan-cta-${p.code}`}>{c.choose}</Link>
  </div>
);

const Pricing = ({ c }) => {
  const [plans, setPlans] = useState([]);
  const [yearly, setYearly] = useState(false);
  useEffect(() => { axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/plans`).then((r) => setPlans(r.data)).catch(() => setPlans([])); }, []);
  const trial = plans.find((p) => p.code === "trial");
  const paid = plans.filter((p) => p.code !== "trial");
  const top = Math.max(...paid.map((p) => p.price_month), 0);
  return (
    <section id="pricing" className="bg-[#F7F7F8] py-24">
      <div className="mx-auto max-w-5xl px-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{c.pricing_title}</h2>
          <p className="mt-3 text-slate-600">{c.pricing_sub}</p>
          <div className="mt-6 inline-flex rounded-full border border-slate-200 bg-white p-1 text-sm" data-testid="landing-billing-toggle">
            {[false, true].map((y) => <button key={String(y)} onClick={() => setYearly(y)} className={`rounded-full px-4 py-1.5 font-medium transition-colors ${yearly === y ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid={`landing-billing-${y ? "yearly" : "monthly"}`}>{y ? c.yearly : c.monthly}{y && <span className="ms-2 rounded-full bg-[#E30613] px-1.5 py-0.5 text-[10px] text-white">{c.save}</span>}</button>)}
          </div>
        </div>
        <div className={`mt-14 grid gap-6 ${paid.length > 2 ? "lg:grid-cols-3" : "md:grid-cols-2"}`}>{paid.map((p) => <PlanCard key={p.code} p={p} c={c} yearly={yearly} hot={p.price_month === top} />)}</div>
        {trial && <p className="mt-10 text-center text-sm text-slate-600" data-testid="landing-trial-banner"><span className="font-bold text-[#E30613]">{trial.trial_days}</span> {c.trial_banner}</p>}
      </div>
    </section>
  );
};

const Testimonials = ({ c, lang }) => (
  <section id="testimonials" className="mx-auto max-w-7xl px-6 py-24">
    <div className="flex flex-wrap items-end justify-between gap-4"><h2 className="text-3xl font-bold tracking-tight text-slate-900">{c.testimonials_title}</h2><span className="text-xs text-slate-400">{c.testimonials_note}</span></div>
    <div className="mt-12 grid gap-6 md:grid-cols-3">
      {(TESTIMONIALS[lang] || TESTIMONIALS.fr).map((x, i) => (
        <figure key={x.name} className={`flex flex-col rounded-2xl border border-slate-200 p-7 ${i === 1 ? "bg-slate-900 text-white md:translate-y-8" : "bg-white"}`} data-testid={`landing-testimonial-${i}`}>
          <Quote className="h-7 w-7 text-[#E30613]" />
          <blockquote className={`mt-4 flex-1 text-[15px] leading-relaxed ${i === 1 ? "text-slate-200" : "text-slate-700"}`}>{x.quote}</blockquote>
          <figcaption className="mt-6 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E30613] text-sm font-bold text-white">{x.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
            <span><span className="block text-sm font-semibold">{x.name}</span><span className={`block text-xs ${i === 1 ? "text-slate-400" : "text-slate-500"}`}>{x.role} · {x.company}</span></span>
          </figcaption>
        </figure>
      ))}
    </div>
  </section>
);

const Faq = ({ c, lang }) => (
  <section id="faq" className="border-t border-slate-200 bg-white py-24">
    <div className="mx-auto grid max-w-6xl gap-10 px-6 lg:grid-cols-[1fr_1.6fr]">
      <h2 className="text-3xl font-bold tracking-tight text-slate-900">{c.faq_title}</h2>
      <Accordion type="single" collapsible className="w-full">
        {(FAQ[lang] || FAQ.fr).map(([q, a], i) => (
          <AccordionItem key={q} value={`q${i}`} data-testid={`landing-faq-${i}`}><AccordionTrigger className="text-start text-base">{q}</AccordionTrigger><AccordionContent className="text-slate-600">{a}</AccordionContent></AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

const FinalCta = ({ c }) => {
  const { t } = useApp();
  return (
    <section className="px-6 pb-24">
      <div className="relative mx-auto max-w-7xl overflow-hidden rounded-3xl bg-slate-900 px-8 py-16 text-white grain sm:px-16">
        <div className="absolute -end-20 -top-20 h-72 w-72 rounded-[28%] bg-[#E30613]" /><div className="absolute end-56 top-10 h-16 w-16 rounded-[28%] bg-[#E30613]/80" />
        <div className="relative max-w-xl">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{c.cta_title}</h2>
          <p className="mt-4 text-slate-300">{c.cta_sub}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/sign-up" data-testid="final-cta-btn" className={CTA}>{t("start_trial")}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180" /></Link>
            <DemoButton testid="final-demo-btn" className="rounded-full border border-white/30 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Landing() {
  const { lang, t } = useApp();
  const c = COPY[lang] || COPY.fr;
  return (
    <div className="min-h-screen scroll-smooth bg-white" data-testid="landing-page">
      <Header c={c} />
      <Hero c={c} />
      <Stats c={c} />
      <InteractiveTour />
      <Showcase c={c} />
      <Features c={c} />
      <Testimonials c={c} lang={lang} />
      <Pricing c={c} />
      <Faq c={c} lang={lang} />
      <FinalCta c={c} />
      <footer className="border-t border-slate-200">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-4 px-6 py-8 text-xs text-slate-500"><Logo size={24} /><span>© 2026 Mohassib · mohassib.tn</span><span className="ms-auto max-w-xl">{t("disclaimer")}</span></div>
      </footer>
    </div>
  );
}
